/**
 * Pagination — điều khiển phân trang dùng chung cho các trang danh sách
 * (Thư viện, Phòng Lab AI, Đạo đức AI, Kiểm chứng AI, …).
 *
 * Hiển thị: ‹ Trước · [1] … [n] · Sau › với cửa sổ số trang gọn (có dấu …).
 * Tự ẩn khi chỉ có 1 trang.
 */

import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PaginationProps {
  /** Trang hiện tại (bắt đầu từ 1). */
  page: number;
  /** Tổng số trang. */
  pageCount: number;
  onChange: (page: number) => void;
  className?: string;
  /** Tổng số mục — nếu truyền sẽ hiện chú thích "x–y / tổng". */
  totalItems?: number;
  /** Số mục mỗi trang — dùng để tính chú thích. */
  pageSize?: number;
}

/** Dựng dải số trang có cửa sổ quanh trang hiện tại + dấu … hai đầu. */
function buildRange(page: number, pageCount: number): (number | '…')[] {
  const delta = 1;
  const range: (number | '…')[] = [];
  const left = Math.max(2, page - delta);
  const right = Math.min(pageCount - 1, page + delta);

  range.push(1);
  if (left > 2) range.push('…');
  for (let i = left; i <= right; i++) range.push(i);
  if (right < pageCount - 1) range.push('…');
  if (pageCount > 1) range.push(pageCount);
  return range;
}

export const Pagination: React.FC<PaginationProps> = ({
  page, pageCount, onChange, className, totalItems, pageSize,
}) => {
  if (pageCount <= 1) return null;
  const pages = buildRange(page, pageCount);

  const btnBase = 'inline-flex items-center justify-center min-w-[34px] h-[34px] px-2 rounded-lg text-sm font-semibold transition-colors';

  const from = totalItems != null && pageSize != null ? (page - 1) * pageSize + 1 : null;
  const to = totalItems != null && pageSize != null ? Math.min(page * pageSize, totalItems) : null;

  return (
    <nav className={`flex flex-wrap items-center justify-center gap-1.5 ${className || ''}`} aria-label="Phân trang">
      {from != null && to != null && (
        <span className="mr-2 text-xs text-slate-400 font-medium">{from}–{to} / {totalItems}</span>
      )}
      <button
        onClick={() => onChange(page - 1)}
        disabled={page <= 1}
        className={`${btnBase} text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed`}
        aria-label="Trang trước"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
      {pages.map((p, i) =>
        p === '…' ? (
          <span key={`e${i}`} className="px-1.5 text-slate-400 select-none">…</span>
        ) : (
          <button
            key={p}
            onClick={() => onChange(p)}
            aria-current={p === page ? 'page' : undefined}
            className={`${btnBase} ${p === page ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}
          >
            {p}
          </button>
        ),
      )}
      <button
        onClick={() => onChange(page + 1)}
        disabled={page >= pageCount}
        className={`${btnBase} text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed`}
        aria-label="Trang sau"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </nav>
  );
};

export default Pagination;
